<template>
  <RmField :field="`hourlyRentAvailable`">
    <label class="flex items-center mb-4 cursor-pointer">
      <RmToggleButton
        :value="hourlyRentAvailable"
        color="#333333"
        @change="onToggleChange"
      />
      <span class="ml-4 font-medium"> Почасовая аренда </span>
    </label>
    <div>
      <div class="mb-2 text-lg font-semibold">Посуточная</div>
      <div
        v-for="(rate, idx) in dailyRates"
        :key="'d' + idx"
      >
        <div class="flex items-start mb-2 gap-2">
          <span class="text-xs mt-3">От</span>
          <RmField
            :error="dailyRateError(idx, 'minDays')"
            :field="`dailyRates.${idx}.minDays`"
            class="w-40"
          >
            <div class="flex items-center pr-2 bg-gray-200 rounded-[12px]">
              <input
                v-model.number="rate.minDays"
                type="number"
                min="1"
                :disabled="idx === 0 && hourlyRentAvailable"
                class="input-price h-10 px-2 text-sm w-full"
                @blur="validateDailyRateField(idx, 'minDays')"
              />
              <span class="px-1 text-xs">{{
                $t('common.days', rate.minDays)
              }}</span>
            </div>
          </RmField>
          <span class="text-xs mt-3 ml-2">цена</span>
          <RmField
            :error="dailyRateError(idx, 'price')"
            :field="`dailyRates.${idx}.price`"
            class="w-40"
          >
            <div class="flex items-center pr-2 bg-gray-200 rounded-[12px]">
              <input
                v-model.number="rate.price"
                type="number"
                min="1"
                class="input-price h-10 px-2 text-sm w-full"
                @blur="validateDailyRateField(idx, 'price')"
              />
              <span class="px-1 text-xs whitespace-nowrap">&nbsp;₽ / день</span>
            </div>
          </RmField>
          <button
            v-if="canRemoveDailyRate(idx)"
            class="ml-2 text-gray-500 hover:text-red-500 p-2"
            @click="removeDailyRate(idx)"
          >
            <TrashIcon />
          </button>
        </div>

        <div v-if="idx === 0">
          <div
            v-if="hourlyRentAvailable"
            class="mb-2 text-xs text-gray-500"
          >
            Если почасовая аренда включена, посуточная аренда должна быть
            доступна от 1-го дня
          </div>
        </div>
        <div
          v-if="idx === minDailyIndex && !hourlyRentAvailable"
          class="mb-2 text-xs text-gray-500"
        >
          Минимальный период аренды
        </div>
      </div>
      <RmBtn
        color="white"
        class="mt-2 rounded-md border-4 border-gray-200"
        @click="addDailyRate"
      >
        <template #icon-left>
          <PlusIcon />
        </template>
        Добавить тариф
      </RmBtn>
    </div>
    <!-- Hourly rates -->
    <div
      v-if="hourlyRentAvailable"
      class="mt-8"
    >
      <div class="mb-2 text-lg font-semibold">Почасовая</div>
      <div class="mb-1 text-xs text-gray-500">Максимальный период почасовой аренды - 8 часов</div>
      <div
        v-for="(rate, idx) in hourlyRates"
        :key="'h' + idx"
      >
        <div class="flex items-start mb-2 gap-2">
          <span class="text-xs mt-3">От</span>
          <RmField
            :error="hourlyRateError(idx, 'minHours')"
            :field="`hourlyRates.${idx}.minHours`"
            class="w-40"
          >
            <div class="flex items-center pr-2 bg-gray-200 rounded-[12px]">
              <input
                v-model.number="rate.minHours"
                type="number"
                min="1"
                max="8"
                class="input-price h-10 px-2 text-sm w-full"
                @blur="validateHourlyRateField(idx, 'minHours')"
              />
              <span class="px-1 text-xs">{{
                $t('common.hours', rate.minHours)
              }}</span>
            </div>
          </RmField>
          <span class="text-xs mt-3">цена</span>
          <RmField
            :error="hourlyRateError(idx, 'price')"
            :field="`hourlyRates.${idx}.price`"
            class="w-40"
          >
            <div class="flex items-center pr-2 bg-gray-200 rounded-[12px]">
              <input
                v-model.number="rate.price"
                type="number"
                min="1"
                class="input-price h-10 px-2 text-sm w-full"
                @blur="validateHourlyRateField(idx, 'price')"
              />
              <span class="px-1 text-xs whitespace-nowrap">&nbsp;₽ / час</span>
            </div>
          </RmField>
          <button
            v-if="canRemoveHourlyRate"
            class="ml-2 text-gray-500 hover:text-red-500 p-2"
            @click="removeHourlyRate(idx)"
          >
            <TrashIcon />
          </button>
        </div>

        <div
          v-if="idx === minHourlyIndex"
          class="mb-2 text-xs text-gray-500"
        >
          Минимальный период аренды
        </div>
      </div>
      <RmBtn
        color="white"
        class="mt-2 rounded-md border-4 border-gray-200"
        @click="addHourlyRate"
      >
        <template #icon-left>
          <PlusIcon />
        </template>
        Добавить тариф
      </RmBtn>
    </div>
    <div
      v-if="showSaveButton"
      class="mt-8"
    >
      <RmBtn
        color="blue"
        class="rounded-md"
        @click="handleSave"
      >
        Обновить цены
      </RmBtn>
    </div>
  </RmField>
</template>

<script setup>
import isEmpty from 'es-toolkit/compat/isEmpty'
import { computed, ref } from 'vue'
import PlusIcon from '~/assets/svg/plus.svg?component'
import TrashIcon from '~/assets/svg/ui/trash.svg?component'
import RmBtn from '~/components/shared/rm_btn.vue'
import RmField from '~/components/shared/rm_field.vue'
import RmToggleButton from '~/components/shared/rm_toggle_button.vue'

const props = defineProps({
  showSaveButton: {
    type: Boolean,
    default: false,
  },
  confirmationMethod: {
    type: Function,
    default: () => {},
  },
})

const dailyRates = ref([])
const hourlyRates = ref([])
const hourlyRentAvailable = ref(false)
const errorsDaily = ref([])
const errorsHourly = ref([])

const lotForm = useLotForm()
const { hasOptionalPrice, isSecureDealActive } = useLotFormSecureDeal()

const canRemoveLastRate = computed(() => !isSecureDealActive.value && hasOptionalPrice.value)

const canRemoveDailyRate = (idx) => {
  if (idx === 0 && hourlyRentAvailable.value) {
    return false
  }
  return dailyRates.value.length > 1 || canRemoveLastRate.value
}

const canRemoveHourlyRate = computed(() =>
  hourlyRates.value.length > 1 || canRemoveLastRate.value,
)

const prices = computed(() => lotForm.item.value?.prices)

const isOptionalPriceEmpty = () => {
  const dailyEmpty = dailyRates.value.every(rate => !rate.price || rate.price <= 0)
  const hourlyEmpty = !hourlyRentAvailable.value
    || hourlyRates.value.every(rate => !rate.price || rate.price <= 0)

  return dailyEmpty && hourlyEmpty
}

const minHourlyValue = computed(() => {
  if (!hourlyRates.value.length) return 1
  return Math.min(...hourlyRates.value.map(r => r.minHours || 1))
})

const minHourlyIndex = computed(() => {
  const min = minHourlyValue.value
  return hourlyRates.value.findIndex(r => (r.minHours || 1) === min)
})

const minDailyValue = computed(() => {
  if (!dailyRates.value.length) return 1
  return Math.min(...dailyRates.value.map(r => r.minDays || 1))
})

const minDailyIndex = computed(() => {
  const min = minDailyValue.value
  return dailyRates.value.findIndex(r => (r.minDays || 1) === min)
})

const initDailyRates = () => {
  dailyRates.value = !isEmpty(prices.value?.dailyRates)
    ? JSON.parse(JSON.stringify(prices.value?.dailyRates))
    : canRemoveLastRate.value
      ? []
      : [{ minDays: 1, price: '' }]

  hourlyRates.value = !isEmpty(prices.value?.hourlyRates)
    ? JSON.parse(JSON.stringify(prices.value?.hourlyRates))
    : canRemoveLastRate.value
      ? []
      : [{ minHours: 1, price: '' }]
  hourlyRentAvailable.value =
      typeof prices.value?.hourlyRentAvailable === 'boolean'
        ? prices.value?.hourlyRentAvailable
        : false
}

const onToggleChange = ({ value }) => {
  hourlyRentAvailable.value = value
  if (value) {
    if (!dailyRates.value.length) {
      dailyRates.value.push({ minDays: 1, price: '' })
    } else {
      dailyRates.value[0].minDays = 1
    }
    if (!hourlyRates.value.length) {
      hourlyRates.value.push({ minHours: 1, price: '' })
    }
  }
}

const addDailyRate = () => {
  dailyRates.value.push({ minDays: '', price: '' })
}

const removeDailyRate = idx => {
  dailyRates.value.splice(idx, 1)
  errorsDaily.value.splice(idx, 1)
}

const validateDailyRateField = (idx, field) => {
  const rate = dailyRates.value[idx]
  if (!errorsDaily.value[idx]) {
    errorsDaily.value[idx] = { minDays: false, price: false }
  }
  if (field === 'minDays') {
    errorsDaily.value[idx].minDays = !rate.minDays || rate.minDays < 1
  }
  if (field === 'price') {
    errorsDaily.value[idx].price = !rate.price || rate.price <= 0
  }
  if (
    !errorsDaily.value[idx].minDays &&
      !errorsDaily.value[idx].price &&
      rate.minDays &&
      rate.price
  ) {
    sortDailyRates()
  }
}

const dailyRateError = (idx, field) => {
  if (!errorsDaily.value[idx]) return ''
  if (field === 'minDays' && errorsDaily.value[idx].minDays)
    return 'Минимум 1 день'
  if (field === 'price' && errorsDaily.value[idx].price) {
    return 'Цена должна быть больше 0'
  }
  return ''
}

const addHourlyRate = () => {
  hourlyRates.value.push({ minHours: '', price: '' })
}

const removeHourlyRate = idx => {
  hourlyRates.value.splice(idx, 1)
  errorsHourly.value.splice(idx, 1)
}

const validateHourlyRateField = (idx, field) => {
  const rate = hourlyRates.value[idx]
  if (!errorsHourly.value[idx]) {
    errorsHourly.value[idx] = { minHours: false, price: false }
  }
  if (field === 'minHours') {
    errorsHourly.value[idx].minHours =
        !rate.minHours || rate.minHours < 1 || rate.minHours > 8
  }
  if (field === 'price') {
    errorsHourly.value[idx].price = !rate.price || rate.price <= 0
  }
  if (
    !errorsHourly.value[idx].minHours &&
      !errorsHourly.value[idx].price &&
      rate.minHours &&
      rate.price
  ) {
    sortHourlyRates()
  }
}

const hourlyRateError = (idx, field) => {
  if (!errorsHourly.value[idx]) return ''
  if (field === 'minHours' && errorsHourly.value[idx].minHours) {
    return 'От 1 до 8 часов'
  }
  if (field === 'price' && errorsHourly.value[idx].price) {
    return 'Цена должна быть больше 0'
  }
  return ''
}

const isValid = () => {
  if (hasOptionalPrice.value && isOptionalPriceEmpty()) {
    return true
  }

  let valid = true
  dailyRates.value.forEach((rate, idx) => {
    validateDailyRateField(idx, 'minDays')
    validateDailyRateField(idx, 'price')
    if (!rate.minDays || rate.minDays < 1 || !rate.price || rate.price <= 0) {
      valid = false
    }
  })
  if (hourlyRentAvailable.value) {
    hourlyRates.value.forEach((rate, idx) => {
      validateHourlyRateField(idx, 'minHours')
      validateHourlyRateField(idx, 'price')
      if (
        !rate.minHours ||
          rate.minHours < 1 ||
          rate.minHours > 8 ||
          !rate.price ||
          rate.price <= 0
      ) {
        valid = false
      }
    })
  }
  if (
    errorsDaily.value.some(e => e && (e.minDays || e.price)) ||
      (hourlyRentAvailable.value &&
        errorsHourly.value.some(e => e && (e.minHours || e.price)))
  ) {
    return false
  }
  return valid
}

const handleSave = async () => {
  if (!isValid()) return false

  const optionalEmpty = hasOptionalPrice.value && isOptionalPriceEmpty()
  const result = await lotForm.lotUpdateHourlyPrices({
    dailyRates: optionalEmpty ? [] : dailyRates.value,
    hourlyRates: optionalEmpty || !hourlyRentAvailable.value ? [] : hourlyRates.value,
    hourlyRentAvailable: optionalEmpty ? false : hourlyRentAvailable.value,
  })

  if (props.confirmationMethod) {
    await props.confirmationMethod(result)
  }

  if (result) {
    initDailyRates()
  }
  return result
}

const sortDailyRates = () => {
  dailyRates.value = [
    ...dailyRates.value
      .filter(r => r.minDays && r.minDays >= 1)
      .sort((a, b) => a.minDays - b.minDays),
    ...dailyRates.value.filter(r => !r.minDays || r.minDays < 1),
  ]
}

const sortHourlyRates = () => {
  hourlyRates.value = [
    ...hourlyRates.value
      .filter(r => r.minHours && r.minHours >= 1)
      .sort((a, b) => a.minHours - b.minHours),
    ...hourlyRates.value.filter(r => !r.minHours || r.minHours < 1),
  ]
}

initDailyRates()

defineExpose({
  isValid,
  handleSave,
})
</script>

<style lang="sass" scoped>
  .input-price
    @apply py-3 w-full border-4 border-gray-200 rounded-[12px]
</style>
